import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Eye,
  Pencil,
  Calendar,
  Search,
  Package,
  User,
  Globe,
  CheckCircle2,
} from "lucide-react";
import { Campaign } from "../../model/Campaign";

const STATUS_LABELS: Record<string, string> = {
  DRAFT: "Rascunho",
  ACTIVE: "Ativa",
  ARCHIVED: "Arquivada",
};

const STATUS_COLORS: Record<string, string> = {
  DRAFT: "bg-gray-100 text-gray-700",
  ACTIVE: "bg-green-100 text-green-800",
  ARCHIVED: "bg-gray-200 text-gray-700",
};

interface ListCampaignItemsProps {
  data: Campaign[];
  filter: string;
  onFilterChange: (filter: string) => void;
  searchTerm: string;
  onSearchChange: (term: string) => void;
  canEdit: boolean;
}

const ListCampaignItems: React.FC<ListCampaignItemsProps> = ({
  data,
  filter,
  onFilterChange,
  searchTerm,
  onSearchChange,
  canEdit,
}) => {
  const navigate = useNavigate();

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      {/* Filters */}
      <div className="p-4 border-b border-gray-100 flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Pesquisar campanhas..."
            aria-label="Pesquisar campanhas"
            className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent"
          />
        </div>
        <div
          className="flex gap-2"
          role="group"
          aria-label="Filtrar por estado"
        >
          {(["all", "ACTIVE", "DRAFT", "ARCHIVED"] as const).map((s) => (
            <button
              key={s}
              onClick={() => onFilterChange(s)}
              aria-pressed={filter === s}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                filter === s
                  ? "bg-blue-600 text-white"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
            >
              {s === "all" ? "Todas" : STATUS_LABELS[s] || s}
            </button>
          ))}
        </div>
      </div>

      {/* List */}
      <div className="divide-y divide-gray-100">
        {data.length === 0 ? (
          <div className="p-12 text-center">
            <Package className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p className="text-gray-500 font-medium">
              Nenhuma campanha encontrada.
            </p>
            <p className="text-gray-400 text-sm mt-1">
              {searchTerm
                ? "Tenta ajustar a pesquisa ou os filtros."
                : "Cria uma nova campanha para começar."}
            </p>
          </div>
        ) : (
          data.map((campaign) => (
            <div
              key={campaign.id}
              className="p-4 flex items-center gap-4 hover:bg-gray-50 transition-colors"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-sm font-semibold text-gray-900 truncate">
                    {campaign.name}
                  </span>
                  <span
                    className={`px-2 py-0.5 rounded text-xs font-medium shrink-0 ${STATUS_COLORS[campaign.status] || "bg-gray-100 text-gray-600"}`}
                  >
                    {STATUS_LABELS[campaign.status] || campaign.status}
                  </span>
                  {campaign.is_currently_active && (
                    <span className="flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-green-50 text-green-700 shrink-0">
                      <CheckCircle2 className="w-3 h-3" />
                      Em vigor
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-3 text-xs text-gray-500">
                  <span className="flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    {new Date(campaign.starts_at).toLocaleDateString("pt-PT")}
                    {" — "}
                    {new Date(campaign.ends_at).toLocaleDateString("pt-PT")}
                  </span>
                  <span>
                    {campaign.items.length}{" "}
                    {campaign.items.length === 1 ? "composto" : "compostos"}
                  </span>
                  {campaign.is_global ? (
                    <span className="flex items-center gap-1">
                      <Globe className="w-3 h-3" />
                      Global
                    </span>
                  ) : (
                    campaign.reseller_erp_ref && (
                      <span className="flex items-center gap-1">
                        <User className="w-3 h-3" />
                        {campaign.reseller_erp_ref}
                      </span>
                    )
                  )}
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <button
                  onClick={() => navigate(`/campaigns/${campaign.id}`)}
                  className="min-h-11 min-w-11 inline-flex items-center justify-center text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                  aria-label={`Ver campanha ${campaign.name}`}
                >
                  <Eye className="w-4 h-4" />
                </button>
                {canEdit && (
                  <button
                    onClick={() =>
                      navigate(`/campaigns/${campaign.id}?edit=true`)
                    }
                    className="min-h-11 min-w-11 inline-flex items-center justify-center text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                    aria-label={`Editar campanha ${campaign.name}`}
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default ListCampaignItems;
